import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './../../api/axios';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function eventLabel(value = '') {
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function IntegrationActivityPanel({ item }) {
  const scope = useMemo(() => item?.activityScope || null, [item]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!scope) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/integrations/config/activity', { params: { ...scope, limit: 20 } });
      setData(response.data || null);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Could not load integration activity.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);
  if (!scope) return null;

  const stats = data?.stats || {};
  const recent = data?.recent || [];
  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Delivery activity</h3>
          <p className="mt-1 text-sm text-gray-500">Safe delivery telemetry for the last events handled by this integration.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 border-b border-gray-100 divide-x divide-gray-100">
        <div className="p-4"><div className="text-xs text-gray-500">Delivered · 24h</div><div className="mt-1 text-xl font-semibold text-gray-950">{stats.delivered24h || 0}</div></div>
        <div className="p-4"><div className="text-xs text-gray-500">Failed · 24h</div><div className={`mt-1 text-xl font-semibold ${(stats.failed24h || 0) > 0 ? 'text-amber-700' : 'text-gray-950'}`}>{stats.failed24h || 0}</div></div>
        <div className="p-4"><div className="text-xs text-gray-500">Last success</div><div className="mt-1 text-sm font-semibold text-gray-900">{formatDate(stats.lastDeliveredAt)}</div></div>
        <div className="p-4"><div className="text-xs text-gray-500">Failure rate · 24h</div><div className="mt-1 text-xl font-semibold text-gray-950">{Math.round((stats.failureRate24h || 0) * 100)}%</div></div>
      </div>

      {error && <div className="m-5 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"><i className="fas fa-triangle-exclamation mr-2" />{error}</div>}
      {!error && recent.length === 0 && !loading && <div className="p-6 text-sm text-gray-500">No delivery activity has been recorded yet.</div>}
      {recent.length > 0 && (
        <div className="divide-y divide-gray-100">
          {recent.map((entry) => (
            <div key={entry._id || `${entry.provider}-${entry.eventType}-${entry.createdAt}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${entry.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : entry.status === 'pending' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-700'}`}>
                <i className={`fas ${entry.status === 'delivered' ? 'fa-check' : entry.status === 'pending' ? 'fa-clock' : 'fa-triangle-exclamation'} text-xs`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{eventLabel(entry.eventType)}</span>
                  {item?.key === 'agent_runtime' && <span className="rounded-md bg-gray-50 px-2 py-0.5 text-[11px] text-gray-500 ring-1 ring-gray-200">{entry.provider}</span>}
                  {(entry.attempts || 1) > 1 && <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 ring-1 ring-amber-200">{entry.attempts} attempts</span>}
                </div>
                <div className="mt-0.5 text-xs text-gray-500 truncate">
                  {entry.payloadSummary?.leadId ? `Lead ${entry.payloadSummary.leadId}` : 'System event'}{entry.payloadSummary?.campaignId ? ` · Campaign ${entry.payloadSummary.campaignId}` : ''}
                </div>
                {entry.error && <div className="mt-1 text-xs text-amber-700 truncate">{entry.error}</div>}
              </div>
              <div className="text-xs text-gray-400 sm:text-right shrink-0">{formatDate(entry.createdAt)}{entry.durationMs != null ? <div>{entry.durationMs} ms</div> : null}</div>
            </div>
          ))}
        </div>
      )}
      {stats.lastError && <div className="border-t border-gray-100 bg-amber-50/50 px-5 py-3 text-xs text-amber-800"><strong>Last error:</strong> {stats.lastError}</div>}
    </section>
  );
}
